import React from 'react';

// Match http(s)://... and www.... URLs, stop at whitespace or common terminators
const URL_REGEX = /\b((?:https?:\/\/|www\.)[^\s<>"')\]]+[^\s<>"')\].,;:!?])/gi;

/**
 * Linkify — splits free-form text and turns URLs into <a> elements.
 * Preserves newlines (use whitespace-pre-wrap on parent container).
 *
 * Pass `linkClassName` to override the default link colour — used by
 * MessagesPage so links on the user's own (navy) bubbles render as
 * white-with-underline instead of navy-on-navy (invisible).
 *
 * Usage:
 *   <Linkify text={article.content} />
 *   <Linkify text={msg.content} linkClassName="text-white underline ..." />
 */
export default function Linkify({ text, className = '', linkClassName = '' }) {
  if (!text) return null;
  const parts = [];
  let lastIndex = 0;
  let match;
  let i = 0;
  // Reset regex state each call
  URL_REGEX.lastIndex = 0;
  const defaultLinkClasses = 'text-brand-navy underline decoration-brand-navy/30 hover:decoration-brand-navy underline-offset-2 break-all';
  while ((match = URL_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    const url = match[0];
    const href = url.startsWith('www.') ? `https://${url}` : url;
    // Show only the hostname inline (e.g. "multimodal.org.uk") instead of the
    // full URL with tracking params. A rich preview card is rendered below
    // the text by <LinkPreviews /> — the URL itself is intentionally hidden.
    let shortLabel = url;
    try { shortLabel = new URL(href).host.replace(/^www\./, ''); } catch { /* keep raw */ }
    parts.push(
      <a
        key={`lk-${i++}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${linkClassName || defaultLinkClasses} ${className}`}
      >
        {shortLabel}
      </a>
    );
    lastIndex = match.index + url.length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return <>{parts}</>;
}
